import React, { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GET_USER_BALANCE } from "../../api/graphql/graph";
import { GET_USER_NOTIFICATIONS } from "../../api/graphql/users";
import { AddFundsModal } from "../Modals/AddFundsModal";
import {
  PaymentResult,
  PaymentResultModal,
} from "../Modals/PaymentResultModal";

// The payment system sends the buyer back to /dashboard?payment=success or
// ?payment=failed (the "success / failure URL" of its project settings).
export const PAYMENT_RETURN_PARAM = "payment";

// The system confirms the payment to the backend callback on its own clock,
// usually after the buyer is already back here. The header loads the balance and
// the bell once as the page opens, so they are refetched a few times after that.
const REFRESH_DELAYS_MS = [5000, 15000, 30000];

const isPaymentResult = (value: string | null): value is PaymentResult =>
  value === "success" || value === "failed";

/**
 * Shows the result of a payment the buyer has just come back from. The URL only
 * decides which modal opens — it proves nothing, so the balance is never
 * touched here; the backend callback credits it and the bell tells about it.
 */
export const PaymentReturn = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const client = useApolloClient();
  const navigate = useNavigate();
  const [result, setResult] = useState<PaymentResult | null>(null);
  const [isResultOpened, setIsResultOpened] = useState(false);
  const [isAddFundsOpened, setIsAddFundsOpened] = useState(false);

  const param = searchParams.get(PAYMENT_RETURN_PARAM);

  useEffect(() => {
    if (!isPaymentResult(param)) return;

    setResult(param);
    setIsResultOpened(true);

    // Keep the address clean so a reload doesn't show the modal again.
    const next = new URLSearchParams(searchParams);
    next.delete(PAYMENT_RETURN_PARAM);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param]);

  useEffect(() => {
    if (!result) return;

    const timers = REFRESH_DELAYS_MS.map((delay) =>
      setTimeout(() => {
        client
          .refetchQueries({
            include: [GET_USER_BALANCE, GET_USER_NOTIFICATIONS],
          })
          .catch(() => undefined);
      }, delay),
    );

    return () => timers.forEach(clearTimeout);
  }, [result, client]);

  if (!result) return null;

  const handleAction = () => {
    setIsResultOpened(false);
    if (result === "success") {
      navigate("/balance");
    } else {
      setIsAddFundsOpened(true);
    }
  };

  return (
    <>
      <PaymentResultModal
        result={result}
        isOpened={isResultOpened}
        closeModal={() => setIsResultOpened(false)}
        onAction={handleAction}
      />
      <AddFundsModal
        isOpened={isAddFundsOpened}
        closeModal={() => setIsAddFundsOpened(false)}
      />
    </>
  );
};

export default PaymentReturn;
